import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatRM, type TodayBreakdownGroupBy, type TodayBreakdownResponse, type TodayReport } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { addDaysKl, klTodayFrom } from '../../lib/dateRange.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { BigButton } from '../../components/BigButton.js';
import { Page, PageHeader, Section } from '../../components/layout.js';
import { SegmentedControl } from '../../components/SegmentedControl.js';
import { BarChart } from '../../components/charts/BarChart.js';
import { PieChart } from '../../components/charts/PieChart.js';

const PAYMENT_LABELS: Record<string, string> = { cash: 'Cash', qr: 'QR', card: 'Card', other: 'Other' };

type ChartView = 'hour' | 'type';
const CHART_VIEW_KEY = 'bazaar.dashboard.chartView';
const GROUP_BY_KEY = 'bazaar.dashboard.groupBy';

function readStoredChartView(): ChartView {
  try {
    const v = localStorage.getItem(CHART_VIEW_KEY);
    return v === 'hour' || v === 'type' ? v : 'hour';
  } catch {
    return 'hour';
  }
}

function readStoredGroupBy(): TodayBreakdownGroupBy {
  try {
    const v = localStorage.getItem(GROUP_BY_KEY);
    return v === 'category' || v === 'product' ? v : 'category';
  } catch {
    return 'category';
  }
}

/**
 * "Sales on Tue, 14 Oct 2026" - a KL calendar date rendered in a readable
 * local form, independent of the viewer's own timezone (it's a label for a
 * date, not an instant, so it's built from UTC year/month/day parts rather
 * than letting the browser reinterpret it).
 */
export function formatReadableDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const utcDate = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(utcDate);
}

export function DashboardPage() {
  // Fixed once per visit (component mount), never persisted - a reload
  // always lands back on today, same as the API's own "no date = today".
  const [today] = useState(() => klTodayFrom(new Date()));
  const [date, setDate] = useState(today);
  const [report, setReport] = useState<TodayReport | null>(null);
  const [breakdown, setBreakdown] = useState<TodayBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartView, setChartView] = useState<ChartView>(readStoredChartView);
  const [groupBy, setGroupBy] = useState<TodayBreakdownGroupBy>(readStoredGroupBy);

  useEffect(() => {
    try {
      localStorage.setItem(CHART_VIEW_KEY, chartView);
    } catch {
      // ignore (private browsing / storage disabled) - the toggle still works for this visit
    }
  }, [chartView]);

  useEffect(() => {
    try {
      localStorage.setItem(GROUP_BY_KEY, groupBy);
    } catch {
      // ignore - see above
    }
  }, [groupBy]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reportRes, breakdownRes] = await Promise.all([
        api.getTodayReport({ date }),
        api.getTodayBreakdown({ groupBy, date }),
      ]);
      setReport(reportRes);
      setBreakdown(breakdownRes);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the dashboard');
    } finally {
      setLoading(false);
    }
  }, [groupBy, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const isToday = date === today;
  const heading = isToday ? "Today's sales" : `Sales on ${formatReadableDate(date)}`;

  function goToDate(next: string) {
    if (next > today) return; // never allow a future date, however it was requested
    setDate(next);
  }

  if (loading && !report) return <Spinner label="Loading dashboard" />;
  if (error) return <EmptyState title="Could not load dashboard" description={error} />;
  if (!report) return null;

  return (
    <Page>
      <PageHeader title="Dashboard" />

      <Section title={heading}>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Choose a date">
          <button
            type="button"
            aria-label="Previous day"
            onClick={() => goToDate(addDaysKl(date, -1))}
            className="min-h-12 min-w-12 shrink-0 rounded-full border-2 border-border bg-surface font-heading text-xl font-semibold text-teal-dark"
          >
            &lsaquo;
          </button>
          <input
            type="date"
            aria-label="Date"
            value={date}
            max={today}
            onChange={(e) => {
              if (e.target.value) goToDate(e.target.value);
            }}
            className="input w-auto max-w-[180px]"
          />
          <button
            type="button"
            aria-label="Next day"
            disabled={isToday}
            onClick={() => goToDate(addDaysKl(date, 1))}
            className="min-h-12 min-w-12 shrink-0 rounded-full border-2 border-border bg-surface font-heading text-xl font-semibold text-teal-dark disabled:opacity-40"
          >
            &rsaquo;
          </button>
          <button
            type="button"
            disabled={isToday}
            onClick={() => goToDate(today)}
            className="min-h-12 shrink-0 rounded-full border-2 border-border bg-surface px-4 font-body text-sm font-semibold text-teal-dark disabled:opacity-40"
          >
            Today
          </button>
        </div>

        <div className="card flex flex-col items-center text-center gap-1">
          <p className="font-body text-sm text-muted">{isToday ? "Today's revenue" : 'Revenue'}</p>
          <p data-testid="dashboard-revenue" className="font-heading font-semibold text-[32px] text-ink">
            {formatRM(report.totals.totalCents)}
          </p>
          <p className="font-body text-sm text-muted">
            {report.totals.saleCount} sale{report.totals.saleCount === 1 ? '' : 's'} &middot; {report.totals.itemCount}{' '}
            item{report.totals.itemCount === 1 ? '' : 's'} sold
            {report.totals.voidedCount > 0 ? ` · ${report.totals.voidedCount} voided` : ''}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="w-full max-w-[220px] sm:w-44">
            <SegmentedControl
              options={[
                { value: 'hour', label: 'By hour' },
                { value: 'type', label: 'By type' },
              ]}
              value={chartView}
              onChange={setChartView}
              ariaLabel="Chart type"
            />
          </div>
          <div className="w-full max-w-[220px] sm:w-44">
            <SegmentedControl
              options={[
                { value: 'category', label: 'Category' },
                { value: 'product', label: 'Product' },
              ]}
              value={groupBy}
              onChange={setGroupBy}
              ariaLabel="Group by"
            />
          </div>
        </div>

        {!breakdown ? (
          <Spinner label="Loading chart" />
        ) : chartView === 'hour' ? (
          <BarChart hours={breakdown.hours} totals={breakdown.totals} groupByLabel={groupBy} />
        ) : (
          <PieChart totals={breakdown.totals} totalQty={breakdown.totalQty} groupByLabel={groupBy} />
        )}
      </Section>

      <Section title="By payment method">
        <div className="chip-row">
          {report.byPaymentMethod.map((m) => (
            <span key={m.method} className="rounded-full bg-aqua text-teal-dark font-body font-semibold text-sm px-3 py-1.5">
              {PAYMENT_LABELS[m.method] ?? m.method}: {formatRM(m.totalCents)} ({m.saleCount})
            </span>
          ))}
        </div>
      </Section>

      {report.byUser && report.byUser.length > 0 && (
        <Section title="By staff">
          <ul className="list-stack">
            {report.byUser.map((u) => (
              // Keyed by uuid + displayName, not userId: a handover keeps
              // one account's uuid/userId the same but splits that day's
              // sales into two snapshot-name rows (see docs/DECISIONS.md),
              // so userId alone isn't unique here.
              <li key={`${u.uuid}-${u.displayName}`} className="card-row flex items-center justify-between">
                <span className="font-body font-semibold text-base text-text">
                  {u.displayName} <span className="text-muted font-normal">({u.shortCode})</span>
                </span>
                <span className="font-body text-sm text-muted">
                  {u.saleCount} sale{u.saleCount === 1 ? '' : 's'}
                </span>
                <span className="font-heading font-semibold text-base text-ink">{formatRM(u.totalCents)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Low stock (current)">
        {!report.lowStock || report.lowStock.length === 0 ? (
          <EmptyState
            title="Nothing is low"
            description="Active products at or below their threshold show here - this always reflects stock right now, regardless of the date selected above."
          />
        ) : (
          <ul className="list-stack">
            {report.lowStock.map((item) => (
              <li key={item.productId}>
                <Link
                  to={`/admin/products/${item.productId}`}
                  className="card-row w-full text-left flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-body font-semibold text-base text-text truncate">{item.name}</p>
                    <p className="font-body text-sm text-muted">{item.code}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-orange text-ink font-heading font-bold text-sm px-2 py-1">
                    {item.stockQty} left
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="btn-row">
        <Link to="/admin/products/new">
          <BigButton>Add product</BigButton>
        </Link>
        <Link to="/admin/products">
          <BigButton variant="outline">Restock</BigButton>
        </Link>
        <Link to="/admin/scan-sheet">
          <BigButton variant="outline">Print scan sheet</BigButton>
        </Link>
      </div>
    </Page>
  );
}
